import React, {useEffect} from 'react';
import {FonomaSEO} from '../../components/seo/fonoma-seo';
import ColoredLink from '../../components/colored-link';
import {getConnectURLLink} from '../util/utilities/get-connect-url-link';
import {Nav} from '../../components/nav/nav';
import {HiddenNavH3} from '../../components/nav/hidden-nav-h3';
import {HiddenNavButton} from '../../components/nav/hidden-nav-button';
import {Header} from '../../components/header';
import {LoaderButton} from '../../components/loader-button';
import {Footer} from '../../components/footer';
import {useBasicBaseTemplate} from './base-template';
import {
    BaseServerData,
    BaseServerExtraData,
    ExtendedLandingPageProps,
} from '../util/landing-props';
import {PromoDetail} from '../../components/conditions/promo-detail-line';
import {PromoDetailsComponent} from '../../components/conditions/promo-details';
import {withTheme} from 'styled-components';
import {PAGE_EVENTS} from '../util/constants';
import isNode from 'detect-node';
import {GradientType} from '../../components/header-noise';
import dynamic from 'next/dynamic';

/*
 * We need to disable server-side rendering (SSR) for components that depend
 * on an external script waiting to load in the browser.
 * https://nextjs.org/docs/messages/react-hydration-error
 */
const LandingsReviewsIoCarouselWidget = dynamic(
    () =>
        import('../../components/reviews-io/reviews-io-carousel-widget').then(
            (mod) => mod.LandingsReviewsIoCarouselWidget
        ),
    {
        ssr: false,
    }
);

/**
 * Extra keys for the dynamic translations
 */

export interface RetentionKeys {
    htmlTitle: string;
    htmlDescription: string;
    title: string;
    subtitle: string;
    hiddenBarTitle: string;
    cta: string;
    announcedCTA?: string;
    buttonHref: string;
    promoTitle?: string;
    fullTermsURLTitle?: string;
    fullTermsURL?: string;
    promoDetails: PromoDetail[];
}

export const _RetentionLandingPageTemplate = <
    LandingServerExtraData extends BaseServerExtraData = BaseServerExtraData,
    LandingNewKeys extends RetentionKeys = RetentionKeys,
>(
    props: ExtendedLandingPageProps<
        BaseServerData<LandingServerExtraData, LandingNewKeys>
    >
) => {
    const {width} = useBasicBaseTemplate();
    const {t, lang} = props.i18n;
    const {
        analytics,
        promo,
        previewMode,
        shouldRender,
        theme,
        operator,
        isAnnounced,
        currentPath,
    } = props;

    useEffect(() => {
        if (!isNode) {
            void analytics.page(undefined, PAGE_EVENTS.LANDING_RETENTION_PAGE, {
                operator,
                isAnnounced,
                promo,
            });
        }
    }, [analytics, isAnnounced, operator, promo]);

    if (!shouldRender) {
        return (
            <>
                <FonomaSEO
                    operator={operator}
                    isAcquisition={false}
                    title={t('landings:impossible-to-render')}
                />
                <h1>{t('landings:page-blank')}</h1>
            </>
        );
    }

    const _previewModeSign = previewMode ? (
        <>
            <ColoredLink fontColor={'green'}>
                {t('common:preview-mode-enabled')}
            </ColoredLink>
            <br />
        </>
    ) : null;

    const {
        htmlTitle,
        htmlDescription,
        title,
        subtitle,
        hiddenBarTitle,
        cta,
        announcedCTA,
        buttonHref,
        promoDetails,
        fullTermsURLTitle,
        fullTermsURL,
        promoTitle,
    } = props.serverData.dynamicTranslations[lang] as RetentionKeys;
    const {landingName} = props.serverData;
    const fullTerms =
        fullTermsURLTitle && fullTermsURL
            ? {
                  title: fullTermsURLTitle,
                  url: fullTermsURL,
              }
            : undefined;

    const changePoint =
        width > 576
            ? theme.desktopMeasures.headerHeight +
              theme.desktopMeasures.navBarHeight
            : theme.mobileMeasures.headerHeight +
              theme.mobileMeasures.navBarHeight;

    const loadingGiftUrl = '/static/landings/miscellanea/spinner-3.gif';

    const onCTAClick = (): void => {
        window.location.assign(
            getConnectURLLink(buttonHref, window.location.href, landingName)
        );
    };

    const getBasicCtaMessage = (announcedCTA: string, cta: string): string => {
        const {isAnnounced} = props;
        return (isAnnounced && announcedCTA) || cta;
    };

    return (
        <>
            <FonomaSEO
                title={htmlTitle}
                description={htmlDescription}
                openGraph={{title: title, description: subtitle}}
                operator={operator}
                isAcquisition={false}
            />

            <Nav changePoint={changePoint} currentPath={currentPath}>
                {hiddenBarTitle && <HiddenNavH3>{hiddenBarTitle}</HiddenNavH3>}
                {(isAnnounced || cta) && (
                    <HiddenNavButton
                        onClickHandler={onCTAClick}
                        loadingGifUrl={loadingGiftUrl}
                    >
                        {getBasicCtaMessage(announcedCTA, cta)}
                    </HiddenNavButton>
                )}
            </Nav>

            <Header
                title={title}
                subtitle={subtitle}
                gradient={GradientType.Retention}
            >
                <LoaderButton
                    onClickHandler={onCTAClick}
                    loadingGifUrl={loadingGiftUrl}
                >
                    {getBasicCtaMessage(announcedCTA, cta)}
                </LoaderButton>
            </Header>

            {promoDetails && (
                <PromoDetailsComponent
                    showBottomBorder={true}
                    showTopBorder={false}
                    promoData={{
                        title: promoTitle,
                        promoDetails: promoDetails,
                        fullTermsURL: fullTerms,
                    }}
                />
            )}

            <LandingsReviewsIoCarouselWidget
                lang={lang}
                dataStore="www.fonoma.com"
            />

            <Footer currentPath={currentPath} />
            {_previewModeSign}
        </>
    );
};

export const RetentionLandingPageTemplate = withTheme(
    _RetentionLandingPageTemplate
);
